import clsx from 'clsx';
import { Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { LANGUAGES, setLanguage } from '../i18n';

export function Logo({ to = '/', light, name }: { to?: string; light?: boolean; name?: string | null }) {
  return (
    <Link to={to} className="flex items-center gap-2" aria-label="High CRM">
      <svg viewBox="0 0 64 64" className="size-8 shrink-0" aria-hidden="true">
        <rect width="64" height="64" rx="14" fill="var(--brand)" />
        <path d="M18 46V18h7v11h14V18h7v28h-7V35H25v11z" fill="#fff" />
        <path d="M44 14l6 6-6 6" stroke="#9CC3FF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className={clsx('truncate text-lg font-semibold tracking-tight', light ? 'text-white' : 'text-slate-900')}>
        {name ?? <>High <span className="text-brand">CRM</span></>}
      </span>
    </Link>
  );
}

export function LanguageSwitcher({ dark, compact }: { dark?: boolean; compact?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={clsx('flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium',
          dark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100')}>
        <Globe className="size-4" />
        {compact ? current.code.toUpperCase() : current.label}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-40 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          {LANGUAGES.map((l) => (
            <button key={l.code} type="button" onClick={() => { setLanguage(l.code); setOpen(false); }}
              className={clsx('w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50',
                l.code === current.code ? 'font-semibold text-brand-700' : 'text-slate-700')}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
